import type { FlyoutManagerState } from './model';
import type { ClaimType } from '@paid-ui/enums/claim';
import type { ContractTemplate } from '@paid-ui/enums/contract';
import type { LinkType } from '@paid-ui/forms/invite-by-link';

import Router from 'next/router';
import { isNil } from 'lodash';

import { ClaimMethod } from '@paid-ui/enums/claim';
import { ContractType } from '@paid-ui/enums/contract';
import { reloadContract } from '@paid-ui/models/contract';
import { reloadProfile } from '@paid-ui/models/user';

import {
  createInitialState,
  EDIT_PARTY_SIGNATORIES_DIALOG,
  flyoutManager,
  FlyoutType,
  globalSettingsDialogs,
  NestedFlyoutType,
} from './model';

export const openFlyout = (
  type: FlyoutType,
  details?: Partial<Omit<FlyoutManagerState, 'open' | 'stack'>>,
) => {
  const { nestedOpen, stack } = flyoutManager;

  Object.assign(flyoutManager, createInitialState(), {
    open: true,
    type,
    // The back-navigation stack is cross-flyout by design: `saveFlyoutState`
    // pushes *before* the target flyout opens, so it must survive this reset or
    // `restoreFlyout` would have nothing left to return to. Only `closeFlyout`
    // clears it.
    stack,
    // FIXME: This is a workaround
    // Save contract draft will close checkout nested flyout
    // Because of the router query changed after draft saved
    nestedOpen:
      type === FlyoutType.SUPER_CONTRACT && details?.details?.contractId
        ? nestedOpen
        : details?.nestedOpen,
    ...details,
  });
};

// Held in a module-level ref rather than the flyout proxy on purpose: openFlyout
// does `Object.assign(flyoutManager, createInitialState(), ...)`, which re-runs on shallow
// URL changes (e.g. after a draft save sets `contractId`) and would otherwise wipe
// a handler whose owner component hasn't remounted, silently killing ESC.
let escapeKeyDownHandler: (() => void) | null = null;

/**
 * Register (or clear, by passing `null`) a custom Escape handler for the open
 * flyout. A flyout with its own close flow uses this so pressing ESC runs the
 * same confirm logic as clicking the close icon, instead of Radix closing the
 * drawer directly and skipping it. GlobalFlyout reads it at keydown time via
 * getFlyoutEscapeKeyDownHandler.
 */
export const setFlyoutEscapeKeyDownHandler = (handler: (() => void) | null) => {
  escapeKeyDownHandler = handler;
};

export const getFlyoutEscapeKeyDownHandler = () => escapeKeyDownHandler;

export const closeFlyout = () => {
  // A fully closed flyout has nowhere to return to, so this is the one reset
  // that also drops the back-navigation stack. Without it, an entry pushed by
  // `saveFlyoutState` and never consumed by `restoreFlyout` would survive here
  // and hijack the close of the next, unrelated flyout (PWA-16081).
  Object.assign(flyoutManager, createInitialState());
  // Clear any registered Escape handler so it can't fire after close. The owner
  // component's unmount cleanup also clears it; this is a defensive backstop.
  escapeKeyDownHandler = null;
};

export const openNestedFlyout = (
  type?: NestedFlyoutType,
  nestedDetails?: Record<string, unknown>,
) => {
  const { open } = flyoutManager;
  if (!open) return;
  flyoutManager.nestedOpen = true;
  flyoutManager.nestedType = type || NestedFlyoutType.NONE;
  flyoutManager.nestedDetails = nestedDetails ?? {};
};

export const closeNestedFlyout = () => {
  flyoutManager.nestedOpen = false;
  flyoutManager.nestedType = NestedFlyoutType.NONE;
};

export const saveFlyoutState = (type: FlyoutType, data?: Record<string, unknown> | null) => {
  flyoutManager.stack.push({
    type,
    pathname: globalThis.location.pathname,
    search: new URLSearchParams(globalThis.location.search).toString(),
    data,
  });
};

export const getFlyoutState = (type?: FlyoutType) => {
  const flyout = flyoutManager.stack.pop();
  if (!flyout) return null;
  if (type && flyout.type !== type) return null;
  return flyout;
};

export const restoreFlyout = () => {
  const previousFlyout = flyoutManager.stack.at(-1);
  if (!previousFlyout) return;
  flyoutManager.stack.pop();
  const { pathname, search } = previousFlyout;
  Router.replace([pathname, search].join('?'), undefined, {
    shallow: true,
  });
};

/**
 * Dismiss the open flyout the way the drawer's own close does: return to
 * whatever pushed this one, or close outright when nothing did.
 *
 * A component rendering its own close/done button should call this rather than
 * `closeFlyout`, which always drops the back-navigation stack and so strands
 * the user on the page instead of the flyout they came from.
 */
export const dismissFlyout = () => {
  if (flyoutManager.stack.length > 0) {
    restoreFlyout();
    return;
  }
  closeFlyout();
};

/**
 * Dismiss a URL-driven flyout by dropping the `dialog` param, which lets the
 * URL-driven effect in `GlobalFlyout` close it. Prefer this over a bare
 * `closeFlyout()` for flyouts opened via `openDialogFromUrl`: closing the
 * drawer without clearing the param leaves a URL that reopens the flyout on
 * refresh or share.
 */
export const closeDialogFromUrl = (removed?: string[]) => {
  const urlSearchParams = new URLSearchParams(globalThis.location.search);
  urlSearchParams.delete('dialog');

  for (const key of removed ?? []) {
    urlSearchParams.delete(key);
  }

  urlSearchParams.sort();
  Router.replace(
    {
      pathname: globalThis.location.pathname,
      search: urlSearchParams.toString(),
    },
    undefined,
    {
      shallow: true,
      scroll: false,
    },
  );
};

const openDialogFromUrl = (dialog: string, query?: Record<string, any>, removed?: string[]) => {
  const urlSearchParams = new URLSearchParams(globalThis.location.search);
  urlSearchParams.set('dialog', dialog);

  if (removed && removed.length > 0) {
    for (const key of removed) {
      urlSearchParams.delete(key);
    }
  }

  if (query) {
    for (const [key, value] of Object.entries(query).filter(([_, value]) => !isNil(value))) {
      if (value) {
        urlSearchParams.set(key, value);
      }
    }
  }

  urlSearchParams.sort();
  Router.replace(
    {
      pathname: globalThis.location.pathname,
      search: urlSearchParams.toString(),
    },
    undefined,
    {
      shallow: true,
      scroll: false,
    },
  );
};

export const openApplyAccountDialog = () =>
  openDialogFromUrl('apply-account', undefined, ['claim', 'claimId']);
export const openKycOnlyDialog = () => openDialogFromUrl('kyc-only');

/**
 * Opens the fork a payee picks their payment account from — their own bank account, or a
 * Paid transaction account. Both routes out of it are the flows above.
 */
export const openPaymentAccountDialog = () => openDialogFromUrl('payment-account');
export const openAccountTransferDialog = () => openDialogFromUrl('account-transfer');

export const openAccountTransferIntraDialog = () => openDialogFromUrl('account-transfer-intra');
export const openTransactionDetailsDialog = (options?: {
  matcherEntryNumber?: string;
  subTab?: string;
}) => openDialogFromUrl('transaction-details', options);

export const openTransactionHistoryDialog = (options: { accountId: string }) =>
  openDialogFromUrl('transaction-history', options);

/**
 * Opens the flow for the bank accounts outside Paid that money is paid out to.
 *
 * The `bank-accounts` value is the one backend emails about an external bank account
 * link to, so a link from an email lands straight on this flyout.
 */
export const openManagePayoutAccountDialog = (options?: { externalBankAccountId?: string }) =>
  openDialogFromUrl('bank-accounts', options);

export const openEditBusinessInfoDialog = () => openDialogFromUrl('edit-business');

export const openFundingDetailsDialog = () => openDialogFromUrl('funding-details');

export const openNewProjectDialog = () => openDialogFromUrl('new-project');
export const openEditProjectDialog = () => openDialogFromUrl('edit-project');
export const openProjectBudgetDialog = (options?: { version?: number }) =>
  openDialogFromUrl('project-budget', options);
export const openProjectDrawnToDateDialog = (options?: { version?: number }) =>
  openDialogFromUrl('project-drawn-to-date', options);
export const openProjectCostToCompleteDialog = (options?: { version?: number }) =>
  openDialogFromUrl('project-cost-to-complete', options);
export const openProjectDrawdownsDialog = (options?: { version?: number }) =>
  openDialogFromUrl('project-drawdowns', options);
export const openProjectLoanAdjustmentsDialog = (options?: { version?: number }) =>
  openDialogFromUrl('project-loan-adjustments', options);

export const openEditProjectBudgetDialog = (options?: { version?: number }) =>
  openDialogFromUrl('edit-project-budget', options, ['version']);

/**
 * Opens the legacy head contract form. Every other contract is created as a super contract.
 */
export const openNewContractDialog = (options?: {
  tab?: string;
  contractTemplate?: ContractTemplate | '';
  claimMethod?: ClaimMethod;
  isDraft?: boolean;
}) => {
  const { contractTemplate, claimMethod, isDraft } = options ?? {};
  openDialogFromUrl('new-contract', {
    tab: options?.tab || 'contracts',
    template: contractTemplate?.toLowerCase(),
    method: claimMethod?.toLowerCase(),
    draft: isDraft ? '1' : '0',
  });
};

export const openAmendContractDialog = (
  contractType: ContractType = ContractType.HEAD_CONTRACT,
  options?: {
    tab?: string;
    contractTemplate?: ContractTemplate;
    claimMethod?: ClaimMethod;
  },
) => {
  const { contractTemplate, claimMethod } = options ?? {};
  openDialogFromUrl('amend-contract', {
    tab: options?.tab || 'contracts',
    contractType,
    template: contractTemplate,
    method: claimMethod,
  });
};

export const openInviteByEmailDialog = (options: { contractId: string; partyId: string }) =>
  openDialogFromUrl('resend-invite', options);

export const openInviteByLinkDialog = (options: {
  contractId: string;
  partyId: string;
  userContractInfoId?: string;
  linkType?: LinkType;
}) => openDialogFromUrl('invite-by-link', options);

export const openInviteFunderDialog = (options?: { partyId?: string }) =>
  openDialogFromUrl('invite-funder', options, ['subTab']);

export const openInviteQSDialog = (options?: { partyId?: string }) =>
  openDialogFromUrl('invite-qs', options, ['subTab']);

export const openAccessLinkedContractsDialog = (options: {
  contractId: string;
  requestId?: string;
}) => openDialogFromUrl('access-linked-contracts', options, ['subTab']);

export const openContractDetailsDialog = (options?: { contractId: string; subTab?: string }) =>
  openDialogFromUrl('contract-details', options, ['subTab']);

export const openUploadSignedContractDialog = (options?: { contractId: string; subTab?: string }) =>
  openDialogFromUrl('upload-signed-contract', options, ['subTab']);

export const openChangePrimaryContactDialog = (options?: {
  contractId: string;
  partyId: string;
  subTab?: string;
}) => openDialogFromUrl('change-primary-contact', options, ['subTab']);

export const openUpdateFeeStructureDialog = (options?: { contractId: string; subTab?: string }) =>
  openDialogFromUrl('update-fee-structure', options, ['subTab']);

export const openAccountDetailsDialog = (options?: { contractId?: string; subTab?: string }) =>
  openDialogFromUrl('account-details', options, ['subTab']);

export const openDepositRefundDialog = (options?: { contractId: string; requestId?: string }) =>
  openDialogFromUrl('deposit-refund', options, ['subTab']);

export const openContractTerminateDialog = (options?: { contractId: string; requestId?: string }) =>
  openDialogFromUrl('terminate-contract', options, ['subTab']);

export const openPaymentRequestDetailsDialog = (options?: { requestId: string }) =>
  openDialogFromUrl('payment-request-details', options, ['subTab']);

export const openSignContractDialog = (options: {
  contractId: string;
  contractTemplate?: ContractTemplate;
}) => openDialogFromUrl('sign-contract', options, ['subTab']);
export const openContractNavigatorDialog = (options: { contractId: string; subTab?: string }) =>
  openDialogFromUrl('contract-navigator', options, ['subTab']);

export const openProjectPartiesDialog = () => openDialogFromUrl('project-parties');

export const openAssignHeldFundsDialog = (options?: { matcherEntryNumber?: string }) =>
  openDialogFromUrl('assign-funds', options, ['subTab']);

export const openClaimAssessmentDialog = (options: {
  contractId: string;
  claimId: string;
  requestId?: string;
}) => openDialogFromUrl('claim-assessment', options, ['subTab']);

export const openRequestAssessmentDialog = (options: {
  contractId: string;
  claimId: string;
  requestId?: string;
}) => openDialogFromUrl('request-assessment', options, ['subTab']);

export const openAbandonPaymentRequestDialog = (options: { requestId: string }) =>
  openDialogFromUrl('abandon-payment-request', options, ['subTab']);

export const openAmendStagedClaimDialog = (options: { paymentId: string }) => {
  openDialogFromUrl(
    'amend-claim',
    {
      method: ClaimMethod.STAGED.toLowerCase(),
      paymentId: options.paymentId,
    },
    ['subTab'],
  );
};

export const openNewClaimDialog = (options?: {
  type?: ClaimType;
  paymentId?: string;
  claimId?: string;
}) => openDialogFromUrl('new-claim', options, ['subTab']);

export const openReviewClaimDialog = (options: { type?: ClaimType; claimId: string }) =>
  openDialogFromUrl('review-claim', options, ['subTab']);

export const openUpdateConsolidatedClaimDialog = (options?: { claimId?: string }) =>
  openDialogFromUrl('update-claim', options, ['subTab']);

export const openSecurePaymentDialog = (options?: { claimId?: string; paymentId?: string }) => {
  openDialogFromUrl('secure-payment', options, ['subTab']);
};

export const openSecureVariationDialog = (options?: { variationId?: string }) => {
  openDialogFromUrl('secure-variation', options, ['subTab']);
};

export const openSecureRequestDialog = (options?: {
  tab?: string;
  contractId: string;
  requestId?: string;
}) => {
  openDialogFromUrl('secure-paid', options, ['subTab']);
};

export const openSecureContractDialog = (options?: { tab?: string; contractId: string }) => {
  openDialogFromUrl('secure-contract', options, ['subTab']);
};

export const openUnsecureRequestDialog = (options?: {
  tab?: string;
  contractId: string;
  requestId?: string;
}) => {
  openDialogFromUrl('unsecure-paid', options, ['subTab']);
};

export const openPayClaimDialog = (options?: { claimId?: string }) => {
  openDialogFromUrl('pay-claim', options, ['subTab']);
};

export const openSubmitRetentionReturnDialog = (options?: { requestId?: string }) =>
  openDialogFromUrl('submit-retention-return', options, ['subTab']);

export const openReviewRetentionReturnDialog = (options: { requestId: string }) =>
  openDialogFromUrl('review-retention-return', options, ['subTab']);

export const openRetentionReturnHistoryDialog = () =>
  openDialogFromUrl('retention-return-history', undefined, ['subTab']);

export const openRetentionReturnDetailsDialog = (options: { requestId: string }) =>
  openDialogFromUrl('retention-return-details', options, ['subTab']);

export const openEarlyReleaseDialog = (options?: { claimId?: string }) => {
  openDialogFromUrl('earlyrelease', options, ['subTab']);
};

export const openReviewOfferDialog = (options?: { claimId?: string }) => {
  openDialogFromUrl('earlyrelease-review-offer', options, ['subTab']);
};

export const openAllClaimsDialog = () => openDialogFromUrl('all-claims');
export const openContractFiltersDialog = () => openDialogFromUrl('contract-filters');
export const openClaimFiltersDialog = () => openDialogFromUrl('claim-filters');
export const openProjectFiltersDialog = () => openDialogFromUrl('project-filters');
export const openUnsharedDraftsDialog = () => openDialogFromUrl('unshared-drafts');

export const openLinkedPaymentsDialog = (options?: { paymentId?: string }) =>
  openDialogFromUrl('linked-payments', options, ['subTab', 'matcherEntryNumber']);
export const openPaymentDetailsDialog = (options?: { paymentId?: string; contractId?: string }) =>
  openDialogFromUrl('payment-details', options, [
    'subTab',
    'matcherEntryNumber',
    'variation',
    'variationId',
    'adjustment',
    'adjustmentId',
  ]);

export const openClaimDetailsDialog = (options?: {
  claimId?: string;
  contractId?: string;
  subTab?: string;
}) =>
  openDialogFromUrl('claim-details', options, [
    'subTab',
    'matcherEntryNumber',
    'variation',
    'variationId',
    'adjustment',
    'adjustmentId',
    'workItem',
    'workItemId',
  ]);

export const openWorkItemDetailsDialog = (options?: {
  workItemId?: string;
  contractId?: string;
  subTab?: string;
}) =>
  openDialogFromUrl('work-item-details', options, [
    'subTab',
    'claim',
    'claimId',
    'transaction',
    'matcherEntryNumber',
    'variation',
    'variationId',
    'adjustment',
    'adjustmentId',
  ]);

export const openNewVariationDialog = (options?: { claimMethod?: ClaimMethod }) => {
  const { claimMethod } = options ?? {};
  openDialogFromUrl('new-variation', {
    method: claimMethod?.toLowerCase(),
  });
};

export const openAmendVariationDialog = (options?: {
  variationId: string;
  claimMethod?: ClaimMethod;
}) => {
  const { variationId, claimMethod } = options ?? {};
  openDialogFromUrl('amend-variation', {
    method: claimMethod?.toLowerCase(),
    variationId,
  });
};

export const openReviewVariationDialog = (options?: {
  variationId: string;
  claimMethod?: ClaimMethod;
}) => {
  const { variationId, claimMethod } = options ?? {};
  openDialogFromUrl(
    'review-variation',
    {
      method: claimMethod?.toLowerCase(),
      variationId,
    },
    ['variation-details'],
  );
};

export const openAllVariationsDialog = () => openDialogFromUrl('all-variations');
export const openVariationDetailsDialog = (options?: { variationId?: string; subTab?: string }) =>
  openDialogFromUrl('variation-details', options, ['subTab', 'claim', 'claimId']);
export const openAdjustmentDetailsDialog = (options?: { adjustmentId?: string; subTab?: string }) =>
  openDialogFromUrl('adjustment-details', options, ['subTab', 'claim', 'claimId']);

export const openHelpCenterDialog = () => openDialogFromUrl('help-center');
export const openAbandonContractDialog = (options: { contractId: string }) =>
  openDialogFromUrl('abandon-contract', options);
export const openDeleteDraftContractDialog = (options: { contractId: string }) =>
  openDialogFromUrl('delete-draft-contract', options);
export const openRevokeContractDialog = (options: { contractId: string }) =>
  openDialogFromUrl('revoke-contract', options);
export const openRecallContractDialog = (options: { contractId: string }) =>
  openDialogFromUrl('recall-contract', options);
export const openUnjoinContractDialog = (options: { contractId: string }) =>
  openDialogFromUrl('unjoin-contract', options);

export const openRetireUserGroupDialog = (options: { userGroupId: string }) =>
  openDialogFromUrl('retire-user-group', options);

export const openEditDirectorStructureDialog = (options: {
  userGroupId: string;
  businessId: string;
}) => openDialogFromUrl('edit-director-structure', options);

export const openSetDefaultSignatoriesDialog = (options: { userGroupId: string }) =>
  openDialogFromUrl('set-default-signatories', options);

export const openEditPartySignatoriesDialog = (options: { contractId: string; partyId: string }) =>
  openDialogFromUrl(EDIT_PARTY_SIGNATORIES_DIALOG, options);

export const openSetCommencementDateDialog = (options: { contractId: string }) =>
  openDialogFromUrl('set-commencement-date', options);
export const openSetCompletionDateDialog = (options: { contractId: string }) =>
  openDialogFromUrl('set-completion-date', options);
export const openTimelineDetailsDialog = (options: { contractId: string }) =>
  openDialogFromUrl('timeline-details', options);
export const openContractDocumentsDialog = (options?: { contractId?: string; subTab?: string }) =>
  openDialogFromUrl('contract-documents', options, ['subTab']);

export const openContractActivityDialog = () => openDialogFromUrl('contract-activity');

export const openContractQSReportsDialog = () => openDialogFromUrl('contract-qs-reports');

export const openContractPaymentsDialog = () => openDialogFromUrl('contract-payments');

export const openContractVariationsDialog = () => openDialogFromUrl('contract-variations');

export const openContractRetentionDialog = () => openDialogFromUrl('contract-retention');

export const openContractFormsDialog = (options: { contractId: string }) =>
  openDialogFromUrl('forms', options);

export const openProjectFormsDialog = () => openDialogFromUrl('project-forms');

export const openRecallClaimDialog = (options: {
  contractId: string;
  claimId: string;
  claimType?: ClaimType;
  isApproved?: boolean;
}) => openDialogFromUrl('recall-claim', options, ['isApproved']);

export const openUpdateClaimAmountDialog = (options: { contractId: string; claimId: string }) =>
  openDialogFromUrl('update-claim-amount', options);

export const openProjectContractsDialog = () =>
  openDialogFromUrl('project-contracts', undefined, ['subTab']);

export const reloadProfileAndCloseFlyout = () => {
  const searchParams = new URLSearchParams(globalThis.location.search);
  const dialog = searchParams.get('dialog');

  if (dialog && globalSettingsDialogs.has(dialog)) {
    // `dismissFlyout` rather than `closeFlyout`: a settings dialog can be reached from
    // another flyout — editing the business name from the payout account flow, say — and
    // closing outright would drop the back-navigation stack and strand the user on the
    // page. With nothing on the stack the two behave identically.
    dismissFlyout();
  } else {
    closeNestedFlyout();
  }

  reloadProfile();
};

export const reloadContractAndCloseFlyout = () => {
  const searchParams = new URLSearchParams(globalThis.location.search);
  const dialog = searchParams.get('dialog');

  if (dialog && globalSettingsDialogs.has(dialog)) {
    closeFlyout();
  } else {
    closeNestedFlyout();
  }

  reloadContract();
};
